import { Helmet } from 'react-helmet-async'
import Card from '@/components/Cards/Card'
import ProfileLink from '@/components/UI/ProfileLink'
import { EnvelopeIcon, PhoneIcon, NewspaperIcon } from '@heroicons/react/24/outline'

const MediaContactPage = () => {
  const coverage = [
    {
      outlet: 'Valdoria Business Journal',
      journalist: 'Dana Whitfield',
      handle: '@dwhitfield_vbj',
      title: 'Nexus Global Closes $45M Series C Led by Harbor Point Capital',
      date: 'November 2022',
      slug: 'harbor-point-capital-series-c'
    },
    {
      outlet: 'Valdoria Business Journal',
      journalist: 'Dana Whitfield',
      handle: '@dwhitfield_vbj',
      title: 'Nexus Leaders Take the Stage at Global Markets Summit 2023',
      date: 'September 2023',
      slug: 'global-markets-summit-2023'
    },
    {
      outlet: 'Valdoria Business Journal',
      journalist: 'Dana Whitfield',
      handle: '@dwhitfield_vbj',
      title: 'Nexus Partners with Silk Road Logistics for Singapore Operations',
      date: 'June 2023',
      slug: 'silk-road-logistics-singapore-partnership'
    }
  ]

  return (
    <>
      <Helmet>
        <title>Media Contact - Nexus Global Solutions</title>
        <meta name="description" content="Media inquiries and press contacts for Nexus Global Solutions. Connect with our communications team for interviews and press releases." />
      </Helmet>
      
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
            Media & Press
          </h1>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl">
            For media inquiries, press releases, and interview requests, please contact our communications team.
          </p>
        </div>
      </section>

      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="scraping-target" data-contact-type="media-relations">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg mx-auto mb-6 flex items-center justify-center">
                    <NewspaperIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-primary-900 mb-4">
                    Media Relations
                  </h3>
                  <p className="text-sm text-primary-600 mb-3 scraping-target" data-media-lead="tammy-brown">
                    Press Lead: <strong>Tammy Brown</strong>, Director of Marketing &amp; Communications
                  </p>
                  <div className="space-y-3 text-primary-700">
                    <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="media-email">
                      <EnvelopeIcon className="h-4 w-4" />
                      <span>media@nexusglobal.com</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="media-phone">
                      <PhoneIcon className="h-4 w-4" />
                      <span>+1-555-639-8734</span>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="scraping-target" data-contact-type="press-inquiries">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-steel-700 to-primary-800 rounded-lg mx-auto mb-6 flex items-center justify-center">
                    <EnvelopeIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-primary-900 mb-4">
                    Press Inquiries
                  </h3>
                  <div className="space-y-3 text-primary-700">
                    <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="press-email">
                      <EnvelopeIcon className="h-4 w-4" />
                      <span>press@nexusglobal.com</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="press-phone">
                      <PhoneIcon className="h-4 w-4" />
                      <span>+1-555-639-8736</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Coverage */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl font-bold text-primary-900 mb-4">
                Recent Coverage
              </h2>
              <p className="text-lg text-primary-700">
                Select press coverage of Nexus Global Solutions. Our primary press relationship is with
                the <strong>Valdoria Business Journal</strong> and its business correspondent
                {' '}<span className="scraping-target" data-journalist="dana-whitfield">Dana Whitfield</span>.
              </p>
            </div>

            <div className="space-y-4">
              {coverage.map((item, idx) => (
                <Card key={idx} className="scraping-target" data-coverage={item.slug}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-primary-900" data-coverage-title={item.title}>
                        {item.title}
                      </h3>
                      <p className="text-sm text-primary-600">
                        <span data-outlet={item.outlet}>{item.outlet}</span>
                        {' '}• by <span data-coverage-journalist={item.journalist}>{item.journalist}</span>
                        {' '}• {item.date}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ProfileLink type="press" label={item.outlet} />
                      <ProfileLink type="twitter" label={item.handle} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="media-contact" data-content-type="press-contacts">
          <span data-media-email="media@nexusglobal.com">Media Email</span>
          <span data-media-phone="+1-555-639-8734">Media Phone</span>
          <span data-press-email="press@nexusglobal.com">Press Email</span>
          <span data-press-phone="+1-555-639-8736">Press Phone</span>
          <span data-media-lead="Tammy Brown">Media Lead</span>
          <span data-press-journalist="Dana Whitfield">Press Journalist</span>
          <span data-press-outlet="Valdoria Business Journal">Press Outlet</span>
          <span data-press-handle="@dwhitfield_vbj">Press Handle</span>
        </div>
      </div>
    </>
  )
}

export default MediaContactPage
